import React, {FC, useEffect} from 'react';
import {
  Badge,
  Brand,
  Divider,
  Heading,
  Icon,
  Motif,
  Paper,
  Text,
} from '../ui';
import './OccasionPage.css';
import {trackEvent} from '../souhait-api';

type Occasion = {
  path: string;
  value: string;
  eyebrow: string;
  title: string;
  description: string;
  example: string;
};

export const occasions: Occasion[] = [
  {
    path: '/cadeau-commun-anniversaire',
    value: 'birthday',
    eyebrow: 'Cadeau commun d’anniversaire',
    title: 'Offrez ensemble le cadeau qui compte vraiment.',
    description:
      'Choisissez un souhait précis, partagez la page au groupe et laissez chacun promettre sa part avant qu’une personne coordonne l’achat.',
    example: 'Un bel objet, un voyage ou une expérience plutôt que plusieurs cadeaux au hasard.',
  },
  {
    path: '/cadeau-pot-de-depart',
    value: 'departure',
    eyebrow: 'Cadeau de départ',
    title: 'Un cadeau de départ qui vous rassemble.',
    description:
      'Rassemblez les promesses autour d’un cadeau précis, gardez les messages avec le souhait et désignez une personne pour l’achat final.',
    example: 'Un souvenir durable, une pièce signée ou une expérience pour la suite.',
  },
  {
    path: '/cadeau-commun-mariage',
    value: 'wedding',
    eyebrow: 'Cadeau commun de mariage',
    title: 'Transformez un grand souhait en cadeau collectif.',
    description:
      'Le couple indique exactement ce qui lui ferait plaisir. Les proches peuvent participer sans créer de compte préalable, puis un capitaine coordonne le cadeau.',
    example: 'Une pièce pour la maison, une escapade ou un objet que le couple gardera longtemps.',
  },
  {
    path: '/cadeau-commun-naissance',
    value: 'birth',
    eyebrow: 'Cadeau commun de naissance',
    title: 'À plusieurs pour le souhait vraiment utile aux parents.',
    description:
      'Une seule page explique le besoin, le modèle et la raison du choix. Les proches promettent leur part sans multiplier les doublons.',
    example: 'Une poussette précise, un meuble durable ou une séance photo de famille.',
  },
  {
    path: '/cadeau-commun-cher',
    value: 'milestone',
    eyebrow: 'Cadeau cher à plusieurs',
    title: 'Un objet trop cher seul devient possible ensemble.',
    description:
      'Ajoutez le lien exact, le prix et ce qui rend cet objet spécial. Le groupe voit la progression sans que l’argent ne bouge avant la coordination finale.',
    example: 'Un appareil photo, un fauteuil, un instrument ou une édition rare.',
  },
  {
    path: '/liste-cadeaux-noel',
    value: 'christmas',
    eyebrow: 'Liste de cadeaux de Noël',
    title: 'Des cadeaux choisis, des surprises préservées.',
    description:
      'Rassemblez les envies au même endroit. Partagez un lien aux proches : chacun réserve son cadeau en privé ou promet une part d’un cadeau commun.',
    example: 'Un livre à garder, une balade ensemble ou un appareil photo offert à plusieurs.',
  },
];

export const occasionForPath = (path: string) =>
  occasions.find(occasion => occasion.path === path.replace(/\/+$/, '')) || null;

const OccasionPage: FC<{occasion: Occasion}> = ({occasion}) => {
  const pageUrl = `https://souha.it${occasion.path}`;
  const pageTitle = `${occasion.eyebrow} pour un souhait précis | souha.it`;
  const campaign = new URLSearchParams(window.location.search).get('campaign');
  const creationUrl = `/me?occasion=${occasion.value}${campaign ? `&campaign=${encodeURIComponent(campaign)}` : ''}`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(
    `${occasion.eyebrow} : ${pageUrl}`
  )}`;

  useEffect(() => {
    document.title = pageTitle;
    trackEvent('page_viewed', 'occasion', {occasion: occasion.value});

    const setMetadata = (selector: string, attribute: string, value: string) => {
      let metadata = document.querySelector<HTMLMetaElement>(selector);
      if (!metadata) {
        metadata = document.createElement('meta');
        const [name, property] = attribute.split('=');
        metadata.setAttribute(name, property);
        document.head.appendChild(metadata);
      }
      metadata.content = value;
    };

    setMetadata('meta[name="description"]', 'name=description', occasion.description);
    setMetadata('meta[property="og:title"]', 'property=og:title', pageTitle);
    setMetadata(
      'meta[property="og:description"]',
      'property=og:description',
      occasion.description
    );
    setMetadata('meta[property="og:url"]', 'property=og:url', pageUrl);
    setMetadata('meta[name="twitter:title"]', 'name=twitter:title', pageTitle);
    setMetadata(
      'meta[name="twitter:description"]',
      'name=twitter:description',
      occasion.description
    );

    let canonical = document.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]'
    );
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = pageUrl;
  }, [occasion, pageTitle, pageUrl]);

  return (
    <main className="occasion-page">
      <header className="occasion-nav">
        <a
          className="occasion-nav__brand"
          href="/"
          aria-label="Accueil souha.it"
        >
          <Brand />
        </a>
        <nav className="occasion-nav__links" aria-label="Navigation de la page">
          <a href="#how-it-works">Comment ça marche</a>
          <a href="#trust">Confiance</a>
        </nav>
        <div className="occasion-nav__actions">
          <a
            className="sui-button sui-button--secondary sui-button--sm"
            href={creationUrl}
          >
            Créer une liste <Icon name="arrow-right" size={16} />
          </a>
        </div>
      </header>

      <section className="occasion-hero">
        <div className="occasion-hero__copy">
          <Badge tone="mustard" icon="gift">{occasion.eyebrow}</Badge>
          <Heading display level={1}>
            {occasion.title}
          </Heading>
          <Text size="lg" tone="muted">
            {occasion.description}
          </Text>
          <div className="occasion-hero__actions">
            <a
              className="sui-button sui-button--primary sui-button--lg"
              href={creationUrl}
            >
              Créer la liste <Icon name="arrow-right" size={18} />
            </a>
            <a
              className="sui-button sui-button--paper sui-button--lg"
              href={whatsappUrl}
              rel="nofollow noopener noreferrer"
              target="_blank"
            >
              <Icon name="share" size={18} /> Partager sur WhatsApp
            </a>
          </div>
          <div className="occasion-hero__proof">
            <Badge tone="leaf" icon="users">
              Un souhait · plusieurs mains
            </Badge>
            <span>Promesses seulement · aucun paiement via souha.it</span>
          </div>
        </div>

        <Paper variant="pinned" className="occasion-example">
          <div className="occasion-example__motifs" aria-hidden="true">
            <Motif name="gift" size={132} />
            <Motif name="heart" size={58} />
            <Motif name="sparkle" size={46} />
          </div>
          <div className="occasion-example__content">
            <Badge tone="sky" icon="gift">Un seul souhait</Badge>
            <Heading level={2}>{occasion.example}</Heading>
            <Text tone="muted">
              Le lien, le prix, l’histoire et les promesses du groupe restent
              réunis au même endroit.
            </Text>
          </div>
        </Paper>
      </section>

      <Divider className="occasion-divider" />

      <section className="occasion-steps" id="how-it-works">
        <div className="occasion-section-heading">
          <Heading level={2}>Une page, trois décisions simples.</Heading>
          <Text tone="muted">
            Un parcours clair pour donner à plusieurs, sans ajouter de compte
            ou de nouveau tableur.
          </Text>
        </div>
        <ol>
          <li>
            <span className="occasion-step__number">01</span>
            <div>
              <Heading level={3}>Choisir le souhait précis.</Heading>
              <Text tone="muted">
                Collez le lien et expliquez en une phrase pourquoi c’est celui-là.
              </Text>
            </div>
            <Badge tone="sky" icon="link">Un lien</Badge>
          </li>
          <li>
            <span className="occasion-step__number">02</span>
            <div>
              <Heading level={3}>Inviter le groupe.</Heading>
              <Text tone="muted">
                Les proches n’ont pas besoin d’un compte souha.it préalable.
                Ouvrez WhatsApp avec le lien prérempli, choisissez vous-même la
                conversation et envoyez le message.
              </Text>
            </div>
            <Badge tone="mustard" icon="users">Pas de compte</Badge>
          </li>
          <li>
            <span className="occasion-step__number">03</span>
            <div>
              <Heading level={3}>Coordonner au bon moment.</Heading>
              <Text tone="muted">
                Un capitaine vérifie la disponibilité et organise l’achat final
                en dehors de souha.it.
              </Text>
            </div>
            <Badge tone="leaf" icon="check">Capitaine</Badge>
          </li>
        </ol>
      </section>

      <section className="occasion-trust" id="trust">
        <Paper variant="tinted" className="occasion-trust__paper">
          <div>
            <Badge tone="sky" icon="heart">
              Surprise préservée
            </Badge>
            <Heading level={2}>
              Participez ensemble, gardez la surprise.
            </Heading>
            <Text size="lg" tone="muted">
              souha.it montre où en est le groupe sans révéler les noms des
              personnes qui offrent. Le paiement
              et l’achat final restent coordonnés par le groupe ; la page
              n’encaisse rien.
            </Text>
            <a
              className="sui-button sui-button--paper sui-button--md"
              href="/julien"
            >
              Voir un exemple de liste <Icon name="arrow-right" size={17} />
            </a>
          </div>
          <div className="occasion-trust__facts">
            <div>
              <strong>01</strong>
              <span>souhait précis</span>
            </div>
            <div>
              <strong>0€</strong>
              <span>encaissé par la page</span>
            </div>
            <div>
              <strong>1</strong>
              <span>capitaine pour coordonner</span>
            </div>
          </div>
        </Paper>
      </section>

      <section className="occasion-offers" aria-label="Choisir son offre">
        <Paper variant="flat">
          <Heading level={2}>Commencez avec dix souhaits gratuits.</Heading>
          <Text tone="muted">
            Une liste et dix souhaits sont gratuits, avec les réservations et
            les promesses de participation. Les invités ne paient aucun abonnement.
          </Text>
          <Text tone="muted">
            Pour une occasion : le Pass à 29€ débloque une liste pendant douze
            mois, sans renouvellement automatique, avec les souhaits illimités
            et un lien vers votre prestataire de paiement externe. Pour plusieurs
            listes : Plus coûte 5€ par mois ou 39€ par an et se renouvelle jusqu’à
            son annulation.
          </Text>
          <a className="sui-button sui-button--secondary sui-button--md" href="/pricing">
            Comparer les offres <Icon name="arrow-right" size={17} />
          </a>
        </Paper>
      </section>

      <nav className="occasion-links" aria-label="Autres occasions">
        <div className="occasion-links__heading">
          <Heading level={2}>D’autres occasions</Heading>
          <Text tone="muted">Choisissez l’occasion qui ressemble au groupe.</Text>
        </div>
        <div className="occasion-links__list">
          {occasions
            .filter(item => item.path !== occasion.path)
            .map(item => (
              <a key={item.path} href={item.path}>
                <span>{item.eyebrow}</span>
                <Icon name="arrow-right" size={16} />
              </a>
            ))}
        </div>
      </nav>

      <footer className="occasion-footer">
        <Brand />
        <nav aria-label="Informations légales">
          <a href="/pricing">Tarifs</a>
          <a href="/privacy">Confidentialité</a>
          <a href="/terms">Conditions</a>
        </nav>
      </footer>
    </main>
  );
};

export default OccasionPage;
